/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import type { FooterColumn } from '@/lib/content/types';

/**
 * One titled column of links in the footer's dark band.
 * Transcribed from Figma "Footer links group" (node `1808:19742`).
 *
 * Content is sourced from a `footer-*-links` content asset (CRCB2B-48), discovered by folder and
 * validated by `footerColumnSchema` — see `src/lib/content/footer-content.server.ts`. This
 * component is pure rendering and does not know where its data came from.
 *
 * Deliberate implementation notes, so they are not "corrected" later:
 *
 * - NO `<nav>` LANDMARK. The enclosing `<footer>` is already a `contentinfo`
 *   landmark, and five nested nav landmarks would be screen-reader noise. The
 *   `<h2>` + `<ul>` structure carries the grouping instead. A `<nav>` would also
 *   need an `aria-label`, and there is no i18n key for one — inventing an
 *   untranslated English label would be worse than the heading structure.
 *
 * - NO `whitespace-nowrap`, though Figma sets it on every label. At the design's
 *   144px column floor, long labels ("Tires & Tubes") would overflow their
 *   column on narrow viewports. Wrapping is the correct responsive behaviour;
 *   the Figma value reflects a fixed-width canvas, not an intent.
 *
 * - Colours come from EXISTING tokens, not new ones. Figma's
 *   `--text-color-on-surface-contrast-secondary` (#eaeaea) for the heading is
 *   `--brand-gray-100` here; `--text-color-on-surface-contrast` (white) for the
 *   items is `--footer-foreground`. No token was added for this footer.
 */
export default function LinkColumn({ heading, links, className }: FooterColumn & { className?: string }): ReactElement {
    return (
        <div className={cn('flex flex-1 flex-col items-start max-w-[180px] min-w-[144px]', className)}>
            {/* Figma "Footer Label" — body/default-bold, 16px, #eaeaea, 8px block padding. */}
            <h2 className="w-full py-2 text-md font-bold tracking-sram text-brand-gray-100">{heading}</h2>

            {/* Figma "Footer Items Container" — 2px between rows, each row 8px block padding. */}
            <ul className="flex flex-col items-start gap-0.5">
                {links.map((link, index) => (
                    // Keyed on url+index: the content asset has no stable per-link id, and a
                    // merchandiser-authored column's link order is stable within one render.
                    // eslint-disable-next-line react/no-array-index-key -- asset-authored links have no stable id
                    <li key={`${link.url}-${index}`} className="w-full">
                        <Link
                            to={link.url}
                            className="block py-2 text-md font-book tracking-sram text-footer-foreground hover:text-brand-gray-300 transition-colors">
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
